(function () {
  'use strict';

  if (window.TUSCartSystemInitialized) {
    return;
  }

  window.TUSCartSystemInitialized = true;

  const shopRoot =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  const cartUrl = `${shopRoot}cart`;
  const cartJsonUrl = `${shopRoot}cart.js`;
  const cartChangeUrl = `${shopRoot}cart/change.js`;

  let drawerRefreshInProgress = false;

  function getDrawerRoot() {
    return document.querySelector('[data-tus-cart-drawer]');
  }

  function openCartDrawer() {
    const drawer = getDrawerRoot();

    if (!drawer) {
      window.location.href = cartUrl;
      return;
    }

    drawer.classList.add('is-open');
    document.body.classList.add('tus-cart-drawer-open');
  }

  function closeCartDrawer() {
    const drawer = getDrawerRoot();

    if (drawer) {
      drawer.classList.remove('is-open');
    }

    document.body.classList.remove('tus-cart-drawer-open');
  }

  function showCartError(message) {
    const errorBoxes = document.querySelectorAll(
      '[data-tus-cart-error]'
    );

    if (!errorBoxes.length) {
      console.error(message);
      return;
    }

    errorBoxes.forEach((box) => {
      box.textContent = message;
      box.style.display = 'block';
    });
  }

  function clearCartErrors() {
    document
      .querySelectorAll('[data-tus-cart-error]')
      .forEach((box) => {
        box.textContent = '';
        box.style.display = 'none';
      });
  }

  function setLineLoading(line, loading) {
    if (!line) {
      return;
    }

    line.style.opacity = loading ? '0.55' : '';
    line.style.pointerEvents = loading ? 'none' : '';
  }

  /**
   * Updates common cart-count elements used by custom themes
   * and many Shopify themes.
   */
  function updateCartCount(itemCount) {
    const textCountSelectors = [
      '[data-cart-count]',
      '[data-tus-cart-count]',
      '.cart-count',
      '#CartCount',
      '.header__cart-count',
      '.cart-count-number',
      '.cart-count-bubble span:first-child'
    ];

    textCountSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        element.textContent = itemCount;
        element.hidden = false;
      });
    });

    document
      .querySelectorAll('.cart-count-bubble')
      .forEach((bubble) => {
        bubble.hidden = itemCount === 0;
        bubble.classList.toggle('hidden', itemCount === 0);
      });

    /**
     * Update links that display text such as Cart (0).
     */
    document
      .querySelectorAll(
        'a[href="/cart"], a[href$="/cart"], [data-tus-cart-open]'
      )
      .forEach((element) => {
        const countElement = element.querySelector(
          '[data-cart-count], [data-tus-cart-count], .cart-count'
        );

        if (countElement) {
          countElement.textContent = itemCount;
        }

        const directText = element.childNodes;

        directText.forEach((node) => {
          if (
            node.nodeType === Node.TEXT_NODE &&
            /cart\s*\(\d+\)/i.test(node.textContent)
          ) {
            node.textContent = node.textContent.replace(
              /cart\s*\(\d+\)/i,
              `Cart (${itemCount})`
            );
          }
        });
      });
  }

  async function getCurrentCart() {
    const response = await fetch(cartJsonUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error('The current cart could not be loaded.');
    }

    return response.json();
  }

  async function refreshCartCount() {
    try {
      const cart = await getCurrentCart();

      updateCartCount(cart.item_count);

      return cart;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Reload only the custom drawer section.
   */
  async function refreshCartDrawer(options = {}) {
    const shouldOpen = options.open !== false;
    const currentSection = document.querySelector(
      '#shopify-section-tus-cart-drawer'
    );

    if (!currentSection || drawerRefreshInProgress) {
      if (shouldOpen && getDrawerRoot()) {
        openCartDrawer();
      }

      return;
    }

    drawerRefreshInProgress = true;

    try {
      const requestUrl =
        `${shopRoot}?sections=tus-cart-drawer`;

      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error('The cart drawer could not be refreshed.');
      }

      const sections = await response.json();
      const drawerHtml = sections['tus-cart-drawer'];

      if (!drawerHtml) {
        throw new Error('Shopify did not return the cart drawer.');
      }

      const parser = new DOMParser();
      const documentFragment = parser.parseFromString(
        drawerHtml,
        'text/html'
      );

      const newSection = documentFragment.querySelector(
        '#shopify-section-tus-cart-drawer'
      );

      if (!newSection) {
        throw new Error(
          'The refreshed cart drawer section was not found.'
        );
      }

      currentSection.replaceWith(newSection);

      bindCartControls();

      if (shouldOpen) {
        openCartDrawer();
      }
    } catch (error) {
      console.error(error);

      if (shouldOpen) {
        window.location.href = cartUrl;
      }
    } finally {
      drawerRefreshInProgress = false;
    }
  }

  async function synchronizeCart(options = {}) {
    const cart = await refreshCartCount();

    if (getDrawerRoot()) {
      await refreshCartDrawer({
        open: options.openDrawer === true
      });
    }

    return cart;
  }

  async function changeCartLine(lineKey, quantity, sourceLine) {
    clearCartErrors();
    setLineLoading(sourceLine, true);

    const safeQuantity = Math.max(
      0,
      parseInt(quantity, 10) || 0
    );

    try {
      const response = await fetch(cartChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: lineKey,
          quantity: safeQuantity
        })
      });

      const cart = await response.json();

      if (!response.ok) {
        throw new Error(
          cart.description ||
            cart.message ||
            'The cart could not be updated.'
        );
      }

      updateCartCount(cart.item_count);

      const isInsideDrawer = Boolean(
        sourceLine &&
          sourceLine.closest('[data-tus-cart-drawer]')
      );

      document.dispatchEvent(
        new CustomEvent('tus:cart-changed', {
          detail: cart
        })
      );

      if (isInsideDrawer) {
        await refreshCartDrawer({
          open: true
        });
      } else {
        /**
         * Reloading the full cart page ensures all Liquid-calculated
         * totals, discounts and shipping progress are correct.
         */
        window.location.reload();
      }
    } catch (error) {
      setLineLoading(sourceLine, false);

      showCartError(
        error.message ||
          'Something went wrong while updating the cart.'
      );
    }
  }

  function handleMinusButton(button) {
    const line = button.closest('[data-line-key]');
    const input = line
      ? line.querySelector('[data-tus-qty-input]')
      : null;

    if (!line || !input) {
      return;
    }

    const currentQuantity =
      parseInt(input.value, 10) || 1;

    const newQuantity = Math.max(
      0,
      currentQuantity - 1
    );

    input.value = newQuantity;

    changeCartLine(
      line.dataset.lineKey,
      newQuantity,
      line
    );
  }

  function handlePlusButton(button) {
    const line = button.closest('[data-line-key]');
    const input = line
      ? line.querySelector('[data-tus-qty-input]')
      : null;

    if (!line || !input) {
      return;
    }

    const currentQuantity =
      parseInt(input.value, 10) || 0;

    const newQuantity = currentQuantity + 1;

    input.value = newQuantity;

    changeCartLine(
      line.dataset.lineKey,
      newQuantity,
      line
    );
  }

  function handleQuantityInput(input) {
    const line = input.closest('[data-line-key]');

    if (!line) {
      return;
    }

    const newQuantity = Math.max(
      0,
      parseInt(input.value, 10) || 0
    );

    input.value = newQuantity;

    changeCartLine(
      line.dataset.lineKey,
      newQuantity,
      line
    );
  }

  function handleRemoveButton(button) {
    const line = button.closest('[data-line-key]');

    if (!line) {
      return;
    }

    changeCartLine(
      line.dataset.lineKey,
      0,
      line
    );
  }

  function bindCartControls() {
    document
      .querySelectorAll('[data-tus-qty-minus]')
      .forEach((button) => {
        if (button.dataset.tusBound === 'true') {
          return;
        }

        button.dataset.tusBound = 'true';

        button.addEventListener('click', () => {
          handleMinusButton(button);
        });
      });

    document
      .querySelectorAll('[data-tus-qty-plus]')
      .forEach((button) => {
        if (button.dataset.tusBound === 'true') {
          return;
        }

        button.dataset.tusBound = 'true';

        button.addEventListener('click', () => {
          handlePlusButton(button);
        });
      });

    document
      .querySelectorAll('[data-tus-qty-input]')
      .forEach((input) => {
        if (input.dataset.tusBound === 'true') {
          return;
        }

        input.dataset.tusBound = 'true';

        input.addEventListener('change', () => {
          handleQuantityInput(input);
        });

        input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            handleQuantityInput(input);
          }
        });
      });

    document
      .querySelectorAll('[data-tus-remove]')
      .forEach((button) => {
        if (button.dataset.tusBound === 'true') {
          return;
        }

        button.dataset.tusBound = 'true';

        button.addEventListener('click', () => {
          handleRemoveButton(button);
        });
      });

    document
      .querySelectorAll('[data-tus-cart-close]')
      .forEach((button) => {
        if (button.dataset.tusBound === 'true') {
          return;
        }

        button.dataset.tusBound = 'true';
        button.addEventListener('click', closeCartDrawer);
      });

    document
      .querySelectorAll('[data-tus-cart-open]')
      .forEach((button) => {
        if (button.dataset.tusBound === 'true') {
          return;
        }

        button.dataset.tusBound = 'true';

        button.addEventListener('click', (event) => {
          event.preventDefault();
          openCartDrawer();
        });
      });
  }

  /**
   * Open the drawer for ordinary cart links, but allow
   * View Full Cart to navigate normally.
   */
  function bindCartLinkInterception() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');

      if (!link) {
        return;
      }

      /**
       * Never intercept the View Full Cart button.
       */
      if (
        link.hasAttribute('data-tus-full-cart-link') ||
        link.classList.contains('tus-drawer-view-cart')
      ) {
        return;
      }

      if (!getDrawerRoot()) {
        return;
      }

      try {
        const clickedUrl = new URL(
          link.href,
          window.location.origin
        );

        const cartPageUrl = new URL(
          cartUrl,
          window.location.origin
        );

        const isCartLink =
          clickedUrl.origin === window.location.origin &&
          clickedUrl.pathname === cartPageUrl.pathname;

        const alreadyOnCartPage =
          window.location.pathname === cartPageUrl.pathname;

        if (isCartLink && !alreadyOnCartPage) {
          event.preventDefault();
          openCartDrawer();
        }
      } catch (error) {
        console.error(error);
      }
    });
  }

  /**
   * Your premium product section emits cart:updated after
   * Add to Cart succeeds. Fetch the real cart so the count
   * and drawer can be updated immediately.
   */
  function bindExternalCartEvents() {
    document.addEventListener('cart:updated', async () => {
      await synchronizeCart({
        openDrawer: true
      });
    });

    document.addEventListener('cart:refresh', async () => {
      await synchronizeCart({
        openDrawer: false
      });
    });

    document.addEventListener('tus:open-cart', () => {
      openCartDrawer();
    });
  }

  function initialize() {
    bindCartControls();
    refreshCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    initialize
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeCartDrawer();
    }
  });

  bindCartLinkInterception();
  bindExternalCartEvents();
})();